import { Injectable, signal } from '@angular/core';
import { NewUiZoneWidgetConfig, is2dArray, isLayoutWidget } from '@finxone-platform/shared/sys-config-types';
import { GenericHandleBarModel } from '../../utils/template/model-builder.utils';
import { WidgetsHideConditionsSelector, hideConditionsRepository } from './hide-conditions-repository';
@Injectable({
  providedIn: 'root',
})
export class HideConditionsService {
  public hideConditionFromWidget = signal<WidgetsHideConditionsSelector>('' as WidgetsHideConditionsSelector);
  public currentModelToCheck = signal<GenericHandleBarModel>({} as GenericHandleBarModel);
  public lastModelToCheck: GenericHandleBarModel = {} as GenericHandleBarModel;
  public currentPageHideConditions = signal<string[]>([]);

  public setHideConditionFromWidget(hideConditionKey: WidgetsHideConditionsSelector) {
    this.hideConditionFromWidget.set(hideConditionKey);
  }

  public extractAndSetCurrentPageWidgetsHideConditions(widgetLayout: NewUiZoneWidgetConfig[]) {
    const hideConditions = this.getHideConditionsFromWidgetLayout(widgetLayout);
    this.setCurrentPageHideConditions(hideConditions);
  }

  private getHideConditionsFromWidgetLayout(widgetLayout: NewUiZoneWidgetConfig[]) {
    let hideConditions: string[] = [];
    widgetLayout.forEach((widget) => {
      hideConditions = [...hideConditions, ...this.collectHideConditionFromWidget(widget)];
    });

    return hideConditions;
  }

  private collectHideConditionFromWidget(widget: NewUiZoneWidgetConfig): string[] {
    const hideConditions: string[] = [];

    // Add hide condition from current widget
    if (widget.attributes?.hideCondition?.length) {
      if (!hideConditions.includes(widget.attributes.hideCondition))
        hideConditions.push(widget.attributes.hideCondition);
    }

    // If it's a layout widget, check for child components
    if (isLayoutWidget(widget.name)) {
      const childComponents = widget?.childComponents ?? [];
      // Handle both simple arrays and 2D arrays
      if (childComponents?.length > 0) {
        // Check if it's a 2D array (array of arrays)
        if (is2dArray(childComponents)) {
          // 2D array - iterate through each row and each widget
          (childComponents as NewUiZoneWidgetConfig[][]).forEach((row) => {
            if (Array.isArray(row)) {
              row.forEach((childWidget) => {
                if (childWidget) {
                  const childConditions = this.collectHideConditionFromWidget(childWidget);
                  hideConditions.push(...childConditions);
                }
              });
            }
          });
        } else {
          // Simple array - iterate through each widget
          (childComponents as NewUiZoneWidgetConfig[]).forEach((childWidget) => {
            if (childWidget) {
              const childConditions = this.collectHideConditionFromWidget(childWidget);
              hideConditions.push(...childConditions);
            }
          });
        }
      }
    }

    // Remove duplicates before returning
    return [...new Set(hideConditions)];
  }

  public setCurrentPageHideConditions(hideConditions: string[]) {
    this.currentPageHideConditions.set(hideConditions);
  }

  public resetCurrentPageHideConditions() {
    this.currentPageHideConditions.set([]);
  }

  public resetHideConditionFromWidget() {
    this.hideConditionFromWidget.set('' as WidgetsHideConditionsSelector);
  }

  public shouldReRenderPageWidgets(): boolean {
    const potentialHideConditions = hideConditionsRepository.get(this.hideConditionFromWidget());
    const currentPageHideConditions = this.currentPageHideConditions();
    if (JSON.stringify(this.lastModelToCheck) === JSON.stringify(this.currentModelToCheck())) {
      return false;
    }
    //if its the first load, dont do a re render
    if (JSON.stringify(this.lastModelToCheck) === JSON.stringify({})) {
      this.lastModelToCheck = this.currentModelToCheck();
      return false;
    }
    this.lastModelToCheck = this.currentModelToCheck();
    if (potentialHideConditions && currentPageHideConditions.length > 0) {
      // Check if any value from potentialHideConditions array exists in currentPageHideConditions
      return potentialHideConditions.some((widgetCondition) =>
        currentPageHideConditions.some((condition) => condition.includes(widgetCondition)),
      );
    }
    return false;
  }
}
